import { Injectable, inject } from '@angular/core';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { Organization } from '../models/organization.model';
import { FirebaseService } from './firebase.service';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private readonly firebase = inject(FirebaseService);

  async getOrganization(orgId: string): Promise<Organization | null> {
    const snap = await getDoc(doc(this.firebase.firestore, 'organizations', orgId));
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      id: snap.id,
      name: data['name'],
      ownerId: data['ownerId'],
      createdAt: data['createdAt'],
      licenseAccepted: data['licenseAccepted'],
      licenseToken: data['licenseToken'],
    };
  }

  // -- License activation (sales-provisioned orgs) --
  // Sales creates the org with `generateLicenseToken: true` and sends the
  // customer an /accept-license link. Accepting consumes the token, makes the
  // caller an admin member, and links the org to their account.

  async verifyLicenseToken(organizationId: string, token: string): Promise<boolean> {
    const callable = httpsCallable<
      { organizationId: string; token: string },
      { verified: boolean }
    >(this.firebase.functions, 'verifyLicenseTokenFromOrganization');
    const result = await callable({ organizationId, token });
    return result.data.verified;
  }

  async acceptLicense(organizationId: string, token: string): Promise<boolean> {
    const callable = httpsCallable<
      { organizationId: string; token: string },
      { success: boolean }
    >(this.firebase.functions, 'acceptLicenseForOrganization');
    const result = await callable({ organizationId, token });
    return result.data.success;
  }
}
